// Consent statement sheet. Shows Gramsci's { hebrew, arabic }, or a fallback message.
// `english` (an array of paragraphs) replaces all of that, left to right — the English page.

import { createElement, useEffect, useMemo, useRef } from 'react';
import { htmlToParagraphs } from '../richText';

const LANGUAGES = [
  ['hebrew', 'he'],
  ['arabic', 'ar'],
];

const MISSING_TERMS = {
  hebrew: '<p>לא ניתן היה לטעון את תנאי השימוש</p>',
  arabic: '<p>تعذّر تحميل شروط الاستخدام</p>',
};

// Never write the non-breaking space as a literal character (see richText.js).
const hasText = (html) => html.replace(/<[^>]*>/g, '').replace(/&nbsp;|\u00a0/g, '').trim() !== '';

function prepare(terms) {
  if (!terms) return null;
  const languages = [];
  for (const [key, lang] of LANGUAGES) {
    const blocks = typeof terms[key] === 'string' ? htmlToParagraphs(terms[key]) : null;
    if (!blocks || !blocks.some((b) => hasText(b.html))) return null;
    languages.push({ lang, blocks });
  }
  return languages;
}

export function resolveConsentTerms(terms) {
  const fromGramsci = prepare(terms);
  return fromGramsci
    ? { source: 'gramsci', languages: fromGramsci }
    : { source: 'missing', languages: prepare(MISSING_TERMS) };
}

export default function TermsModal({ open, onClose, terms = null, english = null }) {
  const { languages } = useMemo(() => resolveConsentTerms(terms), [terms]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);

    // Removing the padding would make the page jump sideways when the dialog opens on
    // Windows, because hiding the scrollbar takes away its ~15px width.
    const gap = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = document.body.style.overflow;
    const prevPadding = document.body.style.paddingRight;
    document.body.style.overflow = 'hidden';
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPadding;
    };
  }, [open, onClose]);

  // Without the pushed history entry, Back would leave the page and lose a half-filled form.
  // Removing the setTimeout would let StrictMode's dev remount pop the entry while the
  // dialog is still open, so the next Back would leave the page.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const pendingBack = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const isSheetEntry = () => Boolean(window.history.state && window.history.state.termsSheet);

    clearTimeout(pendingBack.current);
    if (!isSheetEntry()) window.history.pushState({ ...window.history.state, termsSheet: true }, '');

    const onPop = () => {
      if (!isSheetEntry()) onCloseRef.current();
    };
    window.addEventListener('popstate', onPop);

    return () => {
      window.removeEventListener('popstate', onPop);
      pendingBack.current = setTimeout(() => {
        if (isSheetEntry()) window.history.back();
      }, 0);
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="terms-overlay" onClick={onClose}>
      <div
        className="terms-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={english ? 'Terms and conditions' : 'תנאי השימוש شروط الاستخدام'}
        dir={english ? 'ltr' : 'rtl'}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="terms-close" aria-label={english ? 'Close' : 'סגירה إغلاق'} onClick={onClose}>
          <svg viewBox="0 0 24 24" width="30" height="30" aria-hidden="true" focusable="false">
            <path
              d="M5 5l14 14M19 5L5 19"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {english
          ? english.map((text, j) => <p key={j} className="terms-p" lang="en">{text}</p>)
          : languages.flatMap(({ lang, blocks }) =>
            blocks.map((block, j) =>
              createElement(block.tag, {
                key: `${lang}-${j}`,
                className: 'terms-p',
                lang,
                dangerouslySetInnerHTML: { __html: block.html },
              })
            )
          )}

        <button type="button" className="terms-done" onClick={onClose}>
          {english ? 'Close' : 'סגור أغلق'}
        </button>
      </div>
    </div>
  );
}
